import type { Alignment } from "@/app/_api/generateTts";
import type { Voice } from "@/app/_data/voices";
import type { MultiSpeakerSample } from "@/app/_ui/components/demos/TTS/MultiSpeakerInput";

import * as Sentry from "@sentry/nextjs";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { generateDialogueTts, generateStreamingTts, generateTts } from "@/app/_api/generateTts";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { toast } from "@/app/_providers/toast";
import { useCallback, useRef, useState } from "react";

export type PlayingState = "idle" | "loading" | "paused" | "playing";
export interface TtsGenerationOptions {
  customTtsGenerator?: (params: {
    audioElement: HTMLAudioElement;
    captchaToken?: string;
    config: { text: string };
    modelId: string;
    voiceId: string;
  }) => Promise<void>;
  dialogueModeSamples?: MultiSpeakerSample[];
  isDialogueMode?: boolean;
  modelId: string;
  speed?: number;
  textPrompt?: string;
  ttsAdditionalHeaders?: Record<string, string>;
  ttsBaseUrlOverride?: string;
  useStreaming?: boolean;
  voice?: Voice;
  language?: string;
}

export interface UseTtsGenerationReturn {
  alignment: Alignment | null;
  completeAudioData: Uint8Array | null;
  generate: (options: TtsGenerationOptions & { audioElement: HTMLAudioElement }) => Promise<void>;
  playingState: PlayingState;
  regenerate: boolean;
  setAlignment: (alignment: Alignment | null) => void;
  setPlayingState: (state: PlayingState) => void;
  setRegenerate: (regenerate: boolean) => void;
  setShowLimitReachedOverlay: (show: boolean) => void;
  showLimitReachedOverlay: boolean;
}

const errorMap = {
  quota_exceeded: {
    title: "Limit Reached",
    description: "You've reached the limit for this demo. Please try again later.",
  },
  system_busy: {
    title: "System Busy",
    description: "Our system is currently busy. Please try again in a moment.",
  },
  content_against_policy: {
    title: "Content Against Policy",
    description: "The text you are trying to use may violate our Terms of Service and has been blocked.",
  },
};

export const useTtsGeneration = (
  defaultText: string,
  defaultVoice?: Voice,
  defaultSpeed: number = 1,
  defaultUseStreaming: boolean = true,
  component: "editor_cards" | "tts_omnibox" | "interact_create_omnibox" | "api_omnibox" = "tts_omnibox",
): UseTtsGenerationReturn => {
  const generateAbortControllerRef = useRef<AbortController | null>(null);

  // State
  const [playingState, setPlayingState] = useState<PlayingState>("idle");
  const [alignment, setAlignment] = useState<Alignment | null>(null);
  const [regenerate, setRegenerate] = useState(true);
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);
  const [completeAudioData, setCompleteAudioData] = useState<Uint8Array | null>(null);

  const generate = useCallback(
    async ({
      textPrompt,
      voice,
      language,
      isDialogueMode = false,
      dialogueModeSamples,
      modelId,
      speed = defaultSpeed,
      useStreaming = defaultUseStreaming,
      customTtsGenerator,
      ttsBaseUrlOverride,
      ttsAdditionalHeaders,
      audioElement,
    }: TtsGenerationOptions & { audioElement: HTMLAudioElement }) => {
      if (!audioElement) return;

      setPlayingState("loading");
      setCompleteAudioData(null);

      // abort any previous generation
      generateAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      generateAbortControllerRef.current = abortController;

      setAlignment(null);
      setRegenerate(false);

      let cleanupFunction: (() => void) | undefined = undefined;
      let eventProperties: Record<string, unknown> = {};
      const startedEvent = isDialogueMode
        ? EventName.mw_tts_dialogue_mode_generation_started
        : EventName.landing_page_tts_audio_generated;
      const completedEvent = isDialogueMode
        ? EventName.mw_tts_dialogue_mode_generation_completed
        : EventName.mw_tts_generation_completed;

      if (isDialogueMode) {
        eventProperties = {
          source_component: component,
          tts_mode: "dialogue",
          model_id: modelId,
          text_length: dialogueModeSamples?.reduce((acc, sample) => acc + sample.text.length, 0) || 0,
          speed,
        };
      } else {
        eventProperties = {
          source_component: component,
          tts_mode: "normal",
          voice_id: voice?.voiceId || defaultVoice?.voiceId,
          language,
          model_id: modelId,
          text: textPrompt || defaultText,
          text_length: (textPrompt || defaultText).length,
          speed,
        };
      }

      try {
        captureEvent(startedEvent, eventProperties);
        const token = await getCaptchaToken({ behaviourType: "tts" });

        if (customTtsGenerator) {
          await customTtsGenerator({
            voiceId: voice?.voiceId || defaultVoice?.voiceId || "",
            config: { text: textPrompt || defaultText },
            modelId,
            audioElement,
            captchaToken: token,
          });
          captureEvent(completedEvent, eventProperties);
          // Track that user heard generated audio
          captureEvent(EventName.mw_tts_voice_heard, {
            ...eventProperties,
            audio_type: "generation",
          });
          setPlayingState("idle");
        } else {
          if (isDialogueMode) {
            const samples = (dialogueModeSamples || []).map(sample => ({
              text: sample.text,
              voice_id: sample.voiceId,
            }));

            const response = generateDialogueTts({
              audioElement,
              abortController,
              config: { inputs: samples, hcaptcha_token: token },
            });
            await response.promise;
            cleanupFunction = response.cleanup;
            captureEvent(completedEvent, eventProperties);
            // Track that user heard generated audio
            captureEvent(EventName.mw_tts_voice_heard, {
              ...eventProperties,
              audio_type: "generation",
            });
          } else {
            if (useStreaming) {
              const voiceId = voice?.voiceId || defaultVoice?.voiceId || "";
              const { cleanup, promise } = generateStreamingTts({
                voiceId,
                config: { text: textPrompt || defaultText, language_code: language },
                modelId,
                audioElement,
                speed,
                hcaptcha_token: token,
                urlOverride: ttsBaseUrlOverride ? `${ttsBaseUrlOverride}/${voiceId}/stream` : undefined,
                fallbackUrlOverride: ttsBaseUrlOverride
                  ? `${ttsBaseUrlOverride}/${voiceId}/with-timestamps`
                  : undefined,
                additionalHeaders: ttsAdditionalHeaders,
                onTimestampsReceived: response => {
                  if (response.alignment) {
                    // Update alignment state with new chunks
                    setAlignment(existingAlignment => {
                      if (!existingAlignment) {
                        return response.alignment || null;
                      }
                      if (!response.alignment) {
                        return existingAlignment;
                      }
                      return {
                        character_end_times_seconds: [
                          ...existingAlignment.character_end_times_seconds,
                          ...response.alignment.character_end_times_seconds,
                        ],
                        character_start_times_seconds: [
                          ...existingAlignment.character_start_times_seconds,
                          ...response.alignment.character_start_times_seconds,
                        ],
                        characters: [...existingAlignment.characters, ...response.alignment.characters],
                      };
                    });
                  }
                },
                onStreamComplete: audioData => {
                  setCompleteAudioData(audioData);
                },
                abortController,
              });
              await promise;
              cleanupFunction = cleanup;
              captureEvent(completedEvent, eventProperties);
              // Track that user heard generated audio
              captureEvent(EventName.mw_tts_voice_heard, {
                ...eventProperties,
                audio_type: "generation",
              });
            } else {
              const voiceId = voice?.voiceId || defaultVoice?.voiceId || "";
              const response = await generateTts({
                voiceId,
                config: { text: textPrompt || defaultText, language_code: language },
                modelId,
                audioElement,
                speed,
                withTimestamps: true,
                abortController,
                hcaptcha_token: token,
                urlOverride: ttsBaseUrlOverride ? `${ttsBaseUrlOverride}/${voiceId}/with-timestamps` : undefined,
                additionalHeaders: ttsAdditionalHeaders,
              });
              if (abortController.signal.aborted) return;
              setAlignment(response?.alignment ?? null);
              captureEvent(completedEvent, eventProperties);
              // Track that user heard generated audio
              captureEvent(EventName.mw_tts_voice_heard, {
                ...eventProperties,
                audio_type: "generation",
              });
              audioElement?.play();
            }
          }
        }
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === "AbortError") return;

        const errorEvent = isDialogueMode
          ? EventName.mw_tts_dialogue_mode_generation_error
          : EventName.mw_tts_generation_error;

        const knownApiStatuses = ["quota_exceeded", "system_busy", "content_against_policy"];
        const errorStatus =
          typeof e === "object" && e !== null && "status" in e ? (e as { status: string }).status : null;

        if (errorStatus !== null && knownApiStatuses.includes(errorStatus)) {
          if (errorStatus === "quota_exceeded") {
            captureEvent(EventName.mw_tts_limit_shown, {
              limit_type: "quota",
              ...eventProperties,
            });
            setShowLimitReachedOverlay(true);
          } else {
            captureEvent(errorEvent, {
              ...eventProperties,
              error_status: errorStatus,
            });
            toast.error(errorMap[errorStatus as keyof typeof errorMap].title, {
              description: errorMap[errorStatus as keyof typeof errorMap].description,
            });
          }
        } else {
          captureEvent(errorEvent, {
            ...eventProperties,
            error_status: errorStatus || "unknown",
          });
          toast.error("Something went wrong", {
            description: "It looks like there was an issue while generating. Please try again later.",
          });
          Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
            extra: {
              ...eventProperties,
              error_status: errorStatus ?? "unknown",
            },
          });
        }
        cleanupFunction?.();
        setRegenerate(true); // Reset regenerate flag so user can try again
        setTimeout(() => setPlayingState("idle"), 1000);
      }
    },
    [defaultText, defaultVoice, defaultSpeed, defaultUseStreaming, component],
  );

  return {
    playingState,
    alignment,
    completeAudioData,
    regenerate,
    showLimitReachedOverlay,
    generate,
    setPlayingState,
    setAlignment,
    setRegenerate,
    setShowLimitReachedOverlay,
  };
};
